import {
	Component,
	computed,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	input,
	Input,
	OnInit,
	Output,
	signal,
} from '@angular/core';
import { AuTableRootComponent } from '../au-table-root.component';
import { ICMMSColumn } from '../utils/interfaces';
import { TABLE_ACTIONS } from '../utils/enums';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuRightSidebarService } from '@app/core/services/au-right-sidebar.service';
import { LocationAncestryService } from '@app/shared/services/location-ancestry.service.component';
import {
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { NgClass } from '@angular/common';
import { AuTableCmmsColumnComponent } from '../au-table-cmms-column/au-table-cmms-column.component';
import { MatIcon } from '@angular/material/icon';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { CmmsEntities } from '@app/domain/types';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { ClassificationTypesMenuComponent } from '../au-table-header-components/classification-types-menu/classification-types-menu.component';
import { decrementIndex, incrementIndex } from '@app/core/utils/list-navigation-utils';
import { AuTableActionsColumnComponent } from '../au-table-actions-column/au-table-actions-column/au-table-actions-column.component';

@Component({
	selector: 'au-table',
	templateUrl: './au-table.component.html',
	styleUrls: ['./au-table.component.scss'],
	imports: [
		MatTable,
		NgClass,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		ClassificationTypesMenuComponent,
		AuTableCmmsColumnComponent,
		MatIcon,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		NgxTolgeeModule,
		ClassificationTypesMenuComponent,
		AuTableActionsColumnComponent,
	],
})
export class AuTableComponent extends AuTableRootComponent implements OnInit {
	private readonly sidebarService = inject(AuRightSidebarService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly locationAncestryService = inject(LocationAncestryService);
	private readonly el = inject(ElementRef);

	@Input() set displayedColumns(columns: ICMMSColumn[]) {
		this.tableColumns = columns;
		this.setTableColumns();
	}
	data = input<CmmsEntities[]>([]);
	openedFromActionCenter = input<boolean>(false);
	@Input() usePlaceholderForAvatar = false;
	@Input() actions: TABLE_ACTIONS[] = [];
	@Input() set tableSelection(isTableSelection: boolean) {
		this.isTableSelection = isTableSelection;
		this.setTableColumns();
	}
	@Input() tableSelectedData = new SelectionModel<CmmsEntities>(true, []);
	@Input() isClickableRow = false;
	@Input() numOfStickyColumns: number = 0; // Number of sticky columns from the left

	@Output() delete: EventEmitter<CmmsEntities> = new EventEmitter<CmmsEntities>();
	@Output() edit: EventEmitter<CmmsEntities> = new EventEmitter<CmmsEntities>();
	@Output() reload: EventEmitter<CmmsEntities> = new EventEmitter<CmmsEntities>();
	@Output() selectColumn: EventEmitter<CmmsEntities> = new EventEmitter<CmmsEntities>();
	@Output() tableRowClick: EventEmitter<CmmsEntities> = new EventEmitter<CmmsEntities>();

	@Output() cmmsDelete: EventEmitter<{ data: CmmsEntities }> = new EventEmitter<{
		data: CmmsEntities;
	}>();

	// checkbox output
	@Output() bulkSelection = new EventEmitter<{
		data: CmmsEntities[];
		isAllSelected: boolean;
	}>();
	@Output() rowCheckboxSelect = new EventEmitter<CmmsEntities>();
	@Output() rowCheckboxesSelect = new EventEmitter<CmmsEntities[]>();

	isTableSelection: boolean;
	selection = new SelectionModel<CmmsEntities>(true, []);
	activeElement: CmmsEntities | undefined;
	tableColumns: ICMMSColumn[] = [];
	index = signal(0);
	private shiftKeyPressed = false;
	tableData = computed<CmmsEntities[]>(() =>
		this.data().map(row => {
			if ('location' in row) {
				return {
					...row,
					location: this.locationAncestryService.replaceName(row.location),
				};
			}
			if ('locations' in row) {
				return {
					...row,
					locations: row.locations.map(location =>
						this.locationAncestryService.replaceName(location as ICMMSLocation)
					),
				};
			}
			return row;
		})
	);

	constructor() {
		super();
	}

	@HostListener('window:keydown', ['$event'])
	keyEventDown(event: KeyboardEvent) {
		if (this.openedFromActionCenter()) {
			if (event.key === 'ArrowUp') {
				this.index.set(decrementIndex(this.index(), this.data().length));
			}
			if (event.key === 'ArrowDown') {
				this.index.set(incrementIndex(this.index(), this.data().length));
			}
			if (event.key === ' ') {
				const action = this.tableData()[this.index()];
				return this.tableCheckboxHandler(
					{ source: { checked: true } } as MatCheckboxChange,
					action
				);
			}
			setTimeout(() => {
				this.scrollToActiveElement();
			}, 100);

			return;
		}

		this.shiftKeyPressed = event.shiftKey;
	}

	@HostListener('window:keyup', ['$event'])
	keyEventUp(event: KeyboardEvent) {
		this.shiftKeyPressed = event.shiftKey;
	}

	scrollToActiveElement() {
		const activeElement: HTMLElement = this.el.nativeElement.querySelector('.active');

		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}

	ngOnInit() {
		this.setTableColumns();
		this.subscribeToCloseSidebar();
	}

	setTableColumns() {
		this.columns = this.tableColumns.map(col => col.head);
		this.head = [...this.tableColumns.map(col => col.head)];

		if (this.isTableSelection) {
			this.head = ['select', ...this.head];
		}
	}

	// selection block
	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = this.tableSelectedData?.selected.length;
		const numRows = this.data().length;
		return numSelected === numRows;
	}

	tableBulkCheckboxHandler(event: MatCheckboxChange, data: CmmsEntities[]) {
		const isIndeterminate = event.source.indeterminate;
		if (isIndeterminate) {
			this.bulkSelection.emit({ data, isAllSelected: this.isAllSelected() });
			event.source.checked = false;
		} else {
			this.bulkSelection.emit({ data, isAllSelected: this.isAllSelected() });
		}
	}

	tableCheckboxHandler<T extends CmmsEntities>(event: MatCheckboxChange, data: T) {
		if (this.shiftKeyPressed) {
			// TODO: need to refactor this code block because here we have changing input value directly
			// https://linear.app/autility/issue/SCL-790/refactor-checkbox-selection-in-au-table-component
			const lastCheckedIndex = this.tableData().indexOf(
				this.tableSelectedData.selected[this.tableSelectedData.selected.length - 1]
			);
			const currentIndex = this.tableData().indexOf(data);
			const start = Math.min(lastCheckedIndex, currentIndex);
			const end = Math.max(lastCheckedIndex, currentIndex);

			for (let i = start; i <= end; i++) {
				if (event.source.checked) {
					this.tableSelectedData.select(this.tableData()[i]);
				} else {
					this.tableSelectedData.deselect(this.tableData()[i]);
				}
			}
			this.rowCheckboxesSelect.emit(this.tableSelectedData.selected);
		} else {
			this.rowCheckboxSelect.emit(data);
		}
	}

	tableRowClicked($event: CmmsEntities) {
		if (!this.isClickableRow) {
			return;
		}
		this.activeElement = $event;
		this.tableRowClick.emit($event);
	}

	selectElement($event: CmmsEntities) {
		this.selectColumn.emit($event);
		this.activeElement = $event;
	}

	subscribeToCloseSidebar() {
		this.sidebarService.sidebarIsClosing$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.activeElement = null;
			});
	}
}
